import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../../Services/_states/auth.js';
import useTemplateSx from '../../hooks/useTemplateSx';
import useTemplateTags from '../../hooks/useTemplateTags';
import { radiusPx, spacingFunction } from '../../../../theme/tokens';
import { semantic } from '../../../../theme/tokens/semantic.js';
import {
  CategoryOptions,
  DEFAULT_TEMPLATE_FILTERS,
  LanguageList,
} from '../../utils/templateConstants';
import {
  countActiveFilters,
  getActiveFilterChips,
  lockTemplateOrganisationForUser,
} from '../../utils/templateFilterUtils';
import DateRangeFilter from '../../../../components/Shared/DateRangeFilter/DateRangeFilter.tsx';
import TemplateOrganisationSearchableSelect from '../shared/TemplateOrganisationSearchableSelect';
import TemplateBotNumberSearchableSelect from '../shared/TemplateBotNumberSearchableSelect';
import TemplateSearchableSelect from '../shared/TemplateSearchableSelect';

function buildDraftFromFilters(filters = {}) {
  return {
    organisation: filters.organisation ?? '',
    organisationObj: filters.organisationObj ?? null,
    botNumber: filters.botNumber ?? '',
    botNumberObj: filters.botNumberObj ?? null,
    category: filters.category ?? '',
    categoryObj: filters.categoryObj ?? null,
    tag_id: filters.tag_id ?? '',
    tagObj: filters.tagObj ?? null,
    language: filters.language ?? '',
    languageObj: filters.languageObj ?? null,
  };
}

function preserveTabFilters(filters = {}, patch = {}) {
  return {
    ...patch,
    status: filters.status ?? '',
    statusObj: filters.statusObj ?? null,
    archived: filters.archived ?? 'false',
    archivedObj: filters.archivedObj ?? null,
  };
}

function FilterField({ label, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export default function TemplateFiltersBar({
  filters,
  searchInput,
  onSearchChange,
  onFilterChange,
  onClear,
}) {
  const styles = useTemplateSx();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(() => buildDraftFromFilters(filters));
  const isSuperUser = Boolean(loggedInUser?.is_superuser);
  const loggedInOrganisation = loggedInUser?.profile?.organisation ?? null;
  const loggedInOrganisationId = loggedInOrganisation?.organisation_id ?? '';

  const open = Boolean(anchorEl);
  const activeCount = useMemo(
    () => countActiveFilters(filters, {
      excludeStatus: true,
      excludeArchived: true,
      excludeOrganisation: !isSuperUser,
    }),
    [filters, isSuperUser]
  );
  const activeChips = useMemo(
    () => getActiveFilterChips(filters, {
      excludeStatus: true,
      excludeArchived: true,
      excludeOrganisation: !isSuperUser,
    }),
    [filters, isSuperUser]
  );

  const tagCompanyId =
    draft.organisation || loggedInUser?.profile?.organisation?.organisation_id || null;
  const { tags: tagOptions } = useTemplateTags(open ? tagCompanyId : null);

  const updateDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const handleOpen = (event) => {
    const currentFilters = buildDraftFromFilters(filters);

    if (!isSuperUser) {
      currentFilters.organisation = loggedInOrganisationId;
      currentFilters.organisationObj = loggedInOrganisation;
    }

    setDraft(currentFilters);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onFilterChange(preserveTabFilters(filters, { ...draft, page: 0 }));
    handleClose();
  };

  const handleResetDraft = () => {
    const resetDraft = buildDraftFromFilters(
      lockTemplateOrganisationForUser({
        ...DEFAULT_TEMPLATE_FILTERS,
        limit: filters.limit,
        sortField: filters.sortField,
        sortOrder: filters.sortOrder,
        search: filters.search,
      }, loggedInUser)
    );
    setDraft(resetDraft);
    onFilterChange(preserveTabFilters(filters, { ...resetDraft, page: 0 }));
    handleClose();
  };

  const handleRemoveChip = (chip) => {
    onFilterChange({ ...chip.clearPatch, page: 0 });
  };

  const handleDateRangeApply = (range) => {
    onFilterChange({
      startDate: range?.startDate ?? null,
      endDate: range?.endDate ?? null,
      page: 0,
    });
  };

  return (
    <Box sx={styles.filtersToolbar}>
      <Box sx={{...styles.filtersBar , mt:1 , mb:1.5}}>
        <TextField
          size="small"
          placeholder="Search templates by name..."
          value={searchInput}
          // onChange={(e) => onSearchChange(e.target.value)}
          onChange={(e) => {
            const value = e.target.value
              .replace(/^\s+/, '')
              .replace(/\s{2,}/g, ' ');
            onSearchChange(value);
          }}
          sx={{
            ...styles.filterSearch, '& .MuiOutlinedInput-notchedOutline': {
              top: spacingFunction(0),
            },
            '& .MuiInputBase-root':{borderRadius:radiusPx.md , mb:{xs:1,sm:0}}
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <DateRangeFilter
          onApply={handleDateRangeApply}
          startDate={filters.startDate ?? null}
          endDate={filters.endDate ?? null}
          buttonLabel="Updated date"
        />

        <Badge
          badgeContent={activeCount}
          color="primary"
          invisible={activeCount === 0}
          sx={{ flexShrink: 0 , '& .MuiBadge-badge':{bgcolor: semantic.states.danger} }}
        >
          <Button
            variant={open || activeCount > 0 ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FilterListIcon fontSize="small"/>}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '18px !important' }} />}
            onClick={handleOpen}
            sx={{...styles.filterToggleButton , borderRadius:radiusPx.md}}
          >
            Filters
          </Button>
        </Badge>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { sx: styles.filterPopoverPaper },
          }}
        >
          <Box sx={styles.filterPopoverContent}>
            <Box sx={styles.filterPopoverHeader}>
              <Typography sx={styles.filterPopoverTitle}>Filter templates</Typography>
              <Typography sx={styles.filterPopoverSubtitle}>
                Select criteria and click Apply
              </Typography>
            </Box>

            <Box sx={styles.filterPopoverGrid}>
              <FilterField label="Organisation">
                <TemplateOrganisationSearchableSelect
                  label=""
                  placeholder="All organisations"
                  valueId={draft.organisation || null}
                  selectedOrganisation={draft.organisationObj ?? null}
                  disabled={!isSuperUser}
                  onChange={(option) => {
                    if (!isSuperUser) return;

                    updateDraft({
                      organisation: option?.organisation_id ?? option?.value ?? '',
                      organisationObj: option,
                      botNumber: '',
                      botNumberObj: null,
                      tag_id: '',
                      tagObj: null,
                    });
                  }}
                />
              </FilterField>

              <FilterField label="Bot number">
                <TemplateBotNumberSearchableSelect
                  label=""
                  placeholder="All bot numbers"
                  organisation={draft.organisationObj}
                  valueId={draft.botNumber || null}
                  selectedBot={draft.botNumberObj ?? null}
                  onChange={(option) =>
                    updateDraft({
                      botNumber: option?.wa_phone_number ?? option?.value ?? '',
                      botNumberObj: option,
                    })
                  }
                />
              </FilterField>

              <FilterField label="WhatsApp category">
                <TemplateSearchableSelect
                  variant="filter"
                  label=""
                  placeholder="All categories"
                  options={CategoryOptions}
                  value={draft.categoryObj ?? null}
                  onChange={(option) =>
                    updateDraft({
                      category: option?.value ?? '',
                      categoryObj: option,
                    })
                  }
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                />
              </FilterField>

              <FilterField label="Tags">
                <TemplateSearchableSelect
                  variant="filter"
                  label=""
                  placeholder="All tags"
                  options={tagOptions}
                  value={draft.tagObj ?? null}
                  onChange={(option) =>
                    updateDraft({
                      tag_id: option?.value ?? '',
                      tagObj: option,
                    })
                  }
                  getOptionLabel={(option) => option?.label ?? option?.name ?? ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  disabled={!tagCompanyId}
                />
              </FilterField>

              <FilterField label="Language">
                <TemplateSearchableSelect
                  variant="filter"
                  label=""
                  placeholder="All languages"
                  options={LanguageList}
                  value={draft.languageObj ?? null}
                  onChange={(option) =>
                    updateDraft({
                      language: option?.value ?? '',
                      languageObj: option,
                    })
                  }
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                />
              </FilterField>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={styles.filterPopoverActions}>
              <Button size="small" onClick={handleResetDraft} sx={styles.filterPopoverResetButton}>
                Reset
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleApply}>
                  Apply filters
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      {activeChips.length > 0 ? (
        <Box sx={styles.activeFilterChipsRow}>
          {activeChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              onDelete={() => handleRemoveChip(chip)}
              sx={styles.activeFilterChip}
            />
          ))}
          <Button
            variant="text"
            size="small"
            onClick={onClear}
            sx={styles.filterClearAllLink}
          >
            Clear all
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
